import { Link } from "@/lib/router-compat";
import { Helmet } from "react-helmet-async";
import SiteHeader from "@/components/SiteHeader";
import VideoBanner, { SidebarVideoBanners } from "@/components/blackbox/VideoBanner";
import { BookOpen, AlertTriangle, CheckCircle, HelpCircle } from "lucide-react";

const URL = "https://smothmethod.com/guias/bolhas-na-cabeca-do-penis";

const faq = [
  {
    question: "Bolinha na cabeça do p-nis é DST?",
    answer:
      "Nem sempre. A maioria das bolinhas na cabeça (glande) e na coroa do p-nis são pápulas perladas p-nilianas (PPP) ou glândulas sebáceas — variações anatômicas benignas presentes em 14% a 48% dos homens. Elas não são transmissíveis e não têm relação com atividade sexual. Já bolhas com líquido, dor, ardência ou feridas abertas exigem avaliação médica.",
  },
  {
    question: "Qual a diferença entre bolinha e bolha na cabeça do p-nis?",
    answer:
      "Bolinha é uma elevação sólida da pele (pápula), firme ao toque, cor da pele ou esbranquiçada, geralmente indolor e estável por anos. Bolha (vesícula) contém líquido, é frágil, costuma arder ou doer, aparece de forma súbita em grupos e pode virar ferida. Bolhas verdadeiras não são PPP e precisam de diagnóstico médico.",
  },
  {
    question: "Bolhas na cabeça do p-nis podem ser herpes?",
    answer:
      "Podem. O herpes genital se manifesta como pequenas bolhas agrupadas cheias de líquido, com ardência ou formigamento antes de surgirem, que se rompem e formam feridas dolorosas que cicatrizam em 1 a 2 semanas e reaparecem em surtos. Esse padrão é totalmente diferente das PPP, que são fixas, indolores e permanentes.",
  },
  {
    question: "Bolinhas brancas na coroa do p-nis somem sozinhas?",
    answer:
      "PPP e glândulas de Tyson não desaparecem espontaneamente — são estruturas anatômicas. Podem ficar levemente menos visíveis com a idade, mas não regridem. Já bolinhas causadas por foliculite ou irritação costumam sumir em poucos dias.",
  },
  {
    question: "Espremer as bolinhas da cabeça do p-nis resolve?",
    answer:
      "Não. Espremer não elimina PPP nem glândulas sebáceas e aumenta o risco de infecção, inflamação e cicatriz permanente. A pele da glande é fina e cicatriza mal sob trauma repetido.",
  },
  {
    question: "Bolinhas na cabeça do p-nis com coceira e vermelhidão, o que pode ser?",
    answer:
      "Coceira, vermelhidão difusa e descamação sugerem balanite (inflamação da glande), frequentemente por fungo (Candida) ou irritação química por sabonetes. É tratável e não é uma variação anatômica — procure avaliação médica.",
  },
  {
    question: "Quando devo procurar um urologista?",
    answer:
      "Procure atendimento se houver dor, ardência ao urinar, secreção, feridas abertas, sangramento, bolhas com líquido, crescimento rápido, lesões em couve-flor, febre ou íngua na virilha. Também procure se a bolinha mudou de tamanho, cor ou formato.",
  },
  {
    question: "É possível remover as bolinhas da cabeça do p-nis?",
    answer:
      "Sim, quando são PPP ou glândulas de Tyson. As opções clínicas mais usadas são laser de CO₂ e eletrocauterização, feitas por dermatologista ou urologista, com anestesia local e recuperação de 1 a 3 semanas. A remoção é estética, não médica — a condição é benigna.",
  },
];

const GlansBumpsGuide = () => {
  const medicalSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    headline: "Bolhas e Bolinhas na Cabeça do P-nis: Causas e Quando Se Preocupar",
    description:
      "Guia clínico sobre bolhas e bolinhas na cabeça do p-nis (glande e coroa): PPP, glândulas sebáceas, balanite, herpes e molusco. Como diferenciar e quando procurar médico.",
    author: {
      "@type": "Organization",
      name: "Equipe de Saúde Método P-nis Liso",
      url: "https://smothmethod.com",
    },
    publisher: {
      "@type": "Organization",
      name: "Método P-nis Liso",
      logo: { "@type": "ImageObject", url: "https://smothmethod.com/logo.jpg" },
    },
    datePublished: "2026-08-03",
    dateModified: "2026-08-03",
    lastReviewed: "2026-08-03",
    mainEntityOfPage: { "@type": "WebPage", "@id": URL },
    about: [
      {
        "@type": "MedicalCondition",
        name: "Pápulas Perladas P-nilianas",
        alternateName: ["PPP", "Hirsuties coronae glandis", "Bolinhas na coroa do p-nis"],
        description:
          "Pequenas bolinhas em formato de cúpula dispostas em fileiras na coroa da glande. Variação anatômica benigna em 14% a 48% dos homens.",
        associatedAnatomy: { "@type": "AnatomicalStructure", name: "Coroa da glande" },
      },
      {
        "@type": "MedicalCondition",
        name: "Balanite",
        description:
          "Inflamação da glande com vermelhidão, coceira e descamação, geralmente por fungo ou irritação química.",
        associatedAnatomy: { "@type": "AnatomicalStructure", name: "Glande" },
      },
    ],
    keywords:
      "bolhas na cabeça do p-nis, bolinha na cabeça do p-nis, bolinha no p-nis, bolinhas na coroa do p-nis, bolinhas brancas na glande, glândulas sebáceas no p-nis",
    medicalAudience: { "@type": "MedicalAudience", audienceType: "Patient" },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Início", item: "https://smothmethod.com/" },
      { "@type": "ListItem", position: 2, name: "Guias", item: "https://smothmethod.com/guias/pequenas-bolinhas-no-penis" },
      { "@type": "ListItem", position: 3, name: "Bolhas na Cabeça do P-nis", item: URL },
    ],
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };

  return (
    <div className="min-h-screen bg-[#f6f7f9]">
      <SidebarVideoBanners />
      <Helmet>
        <title>Bolhas na Cabeça do P-nis: Causas e o Que Fazer (2026)</title>
        <meta
          name="description"
          content="Bolhas e bolinha na cabeça do p-nis: como diferenciar PPP, glândulas sebáceas, balanite e herpes. Guia clínico com tabela comparativa e sinais de alerta."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={URL} />
        <link rel="alternate" hrefLang="pt-br" href={URL} />
        <link rel="alternate" hrefLang="x-default" href={URL} />
        <meta property="og:locale" content="pt_BR" />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="Bolhas na Cabeça do P-nis: Causas e o Que Fazer" />
        <meta
          property="og:description"
          content="Como diferenciar bolinhas benignas (PPP e glândulas sebáceas) de bolhas que exigem médico. Guia clínico em pt-BR."
        />
        <meta property="og:url" content={URL} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Bolhas na Cabeça do P-nis: Causas e o Que Fazer" />
        <meta
          name="twitter:description"
          content="PPP, glândulas sebáceas, balanite ou herpes? Tabela comparativa e sinais de alerta."
        />
        <script type="application/ld+json">{JSON.stringify(medicalSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <SiteHeader />

      <main className="max-w-3xl mx-auto px-4 py-8">
        <nav className="mb-6 text-sm" aria-label="Breadcrumb">
          <ol className="flex items-center gap-2 text-gray-500">
            <li><Link to="/" className="hover:text-[#4267B2]">Início</Link></li>
            <li>/</li>
            <li><Link to="/guias/pequenas-bolinhas-no-penis" className="hover:text-[#4267B2]">Guias</Link></li>
            <li>/</li>
            <li className="text-gray-700 font-medium">Bolhas na Cabeça do P-nis</li>
          </ol>
        </nav>

        <article className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <header className="p-6 md:p-8 bg-gradient-to-r from-[#4267B2]/5 to-[#4267B2]/10 border-b border-gray-100">
            <div className="flex items-center gap-2 mb-4">
              <BookOpen className="w-5 h-5 text-[#4267B2]" />
              <span className="text-sm font-medium text-[#4267B2]">Guia Clínico</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight mb-4">
              Bolhas e Bolinha na Cabeça do P-nis: Causas, Diferenças e Quando Se Preocupar
            </h1>
            <p className="text-gray-600">
              Bolinhas na cabeça do p-nis quase sempre são anatomia normal. Bolhas com líquido, não. Veja como diferenciar em 2 minutos.
            </p>
            <div className="flex items-center gap-4 mt-4 text-sm text-gray-500">
              <span>Atualizado: Agosto de 2026</span>
              <span>•</span>
              <span>10 min de leitura</span>
            </div>
          </header>

          <div className="p-6 md:p-8">
            <nav className="bg-gray-50 rounded-lg p-4 mb-8">
              <h2 className="font-semibold text-gray-900 mb-3">Sumário</h2>
              <ol className="space-y-2 text-sm list-decimal list-inside">
                <li><a href="#resumo" className="text-[#4267B2] hover:underline">Resposta rápida</a></li>
                <li><a href="#bolinha-x-bolha" className="text-[#4267B2] hover:underline">Bolinha x bolha: a diferença que muda tudo</a></li>
                <li><a href="#causas" className="text-[#4267B2] hover:underline">Causas mais comuns</a></li>
                <li><a href="#tabela" className="text-[#4267B2] hover:underline">Tabela comparativa</a></li>
                <li><a href="#alerta" className="text-[#4267B2] hover:underline">Sinais de alerta</a></li>
                <li><a href="#remocao" className="text-[#4267B2] hover:underline">Remoção das bolinhas benignas</a></li>
                <li><a href="#faq" className="text-[#4267B2] hover:underline">Perguntas frequentes</a></li>
              </ol>
            </nav>

            <section id="resumo" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-3">1. Resposta rápida</h2>
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-4">
                <p className="text-gray-800">
                  <strong>Bolinha no p-nis</strong> — pequena, firme, indolor, cor da pele ou esbranquiçada, em fileiras ao redor da
                  coroa da glande — é, na grande maioria dos casos, <strong>pápula perlada p-niliana (PPP)</strong> ou
                  <strong> glândula sebácea</strong>: benigna, não contagiosa e sem relação com DST.
                  Já <strong>bolhas na cabeça do p-nis</strong> — com líquido, ardência, dor ou feridas — não são anatomia normal
                  e precisam de avaliação médica.
                </p>
              </div>
              <p className="text-gray-700">
                Estudos clínicos estimam que entre 14% e 48% dos homens têm PPP, com maior frequência em não circuncidados. É uma
                das dúvidas mais comuns em consultas de urologia e, na prática, o motivo número um de pânico desnecessário.
              </p>
            </section>

            <section id="bolinha-x-bolha" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-3">2. Bolinha x bolha: a diferença que muda tudo</h2>
              <p className="text-gray-700 mb-4">
                A confusão entre "bolinha" e "bolha" é o que faz muita gente procurar informação errada. Em termos clínicos:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" /> Bolinha (pápula)
                  </h3>
                  <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                    <li>Sólida, firme ao toque</li>
                    <li>Indolor, sem coceira</li>
                    <li>Tamanho estável por anos</li>
                    <li>Distribuição simétrica, em fileiras</li>
                    <li>Geralmente PPP, Tyson ou Fordyce</li>
                  </ul>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600" /> Bolha (vesícula)
                  </h3>
                  <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                    <li>Contém líquido, frágil</li>
                    <li>Arde, dói ou formiga</li>
                    <li>Surge de repente, em grupos</li>
                    <li>Pode romper e virar ferida</li>
                    <li>Exige diagnóstico médico</li>
                  </ul>
                </div>
              </div>
            </section>

            <section id="causas" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-3">3. Causas mais comuns</h2>

              <h3 className="font-semibold text-gray-900 mt-4 mb-2">Pápulas perladas p-nilianas (PPP)</h3>
              <p className="text-gray-700 mb-3">
                Bolinhas de 1 a 3 mm, cor da pele ou perolada, alinhadas em uma ou duas fileiras na coroa da glande. Benignas,
                permanentes e não transmissíveis. Leia mais em{" "}
                <Link to="/artigos/ppp-explicada" className="text-[#4267B2] hover:underline">PPP explicada</Link>.
              </p>

              <h3 className="font-semibold text-gray-900 mt-4 mb-2">Glândulas de Tyson e glândulas sebáceas</h3>
              <p className="text-gray-700 mb-3">
                Glândulas sebáceas modificadas no frênulo e prepúcio, visíveis como pontinhos brancos ou amarelados. Produzem
                lubrificação natural. Detalhes em{" "}
                <Link to="/artigos/glandulas-de-tyson-explicadas" className="text-[#4267B2] hover:underline">glândulas de Tyson explicadas</Link>.
              </p>

              <h3 className="font-semibold text-gray-900 mt-4 mb-2">Manchas de Fordyce</h3>
              <p className="text-gray-700 mb-3">
                Pontos amarelados na haste, presentes em até 80% dos adultos. Benignos e sem tratamento necessário.
              </p>

              <h3 className="font-semibold text-gray-900 mt-4 mb-2">Balanite</h3>
              <p className="text-gray-700 mb-3">
                Inflamação da glande com vermelhidão difusa, coceira, descamação e às vezes odor. Costuma ter causa fúngica
                (Candida) ou irritativa (sabonete, preservativo). Tem tratamento.
              </p>

              <h3 className="font-semibold text-gray-900 mt-4 mb-2">Herpes genital</h3>
              <p className="text-gray-700 mb-3">
                Bolhas agrupadas com líquido, dolorosas, que rompem e formam úlceras. Episódios recorrentes. Requer avaliação e
                tratamento antiviral.
              </p>

              <h3 className="font-semibold text-gray-900 mt-4 mb-2">Verrugas genitais (HPV) e molusco contagioso</h3>
              <p className="text-gray-700">
                Verrugas têm superfície irregular, aspecto de couve-flor e crescem com o tempo. Molusco forma bolinhas com
                depressão central (umbilicadas). Ambos são contagiosos. Compare em{" "}
                <Link to="/artigos/ppp-vs-verrugas-genitais" className="text-[#4267B2] hover:underline">PPP vs verrugas genitais</Link>.
              </p>
            </section>

            <section id="tabela" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-3">4. Tabela comparativa</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-gray-200">
                  <caption className="sr-only">
                    Comparação entre PPP, glândulas sebáceas, herpes, HPV e balanite na cabeça do p-nis
                  </caption>
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="text-left p-2 border-b border-gray-200">Condição</th>
                      <th scope="col" className="text-left p-2 border-b border-gray-200">Aparência</th>
                      <th scope="col" className="text-left p-2 border-b border-gray-200">Sintomas</th>
                      <th scope="col" className="text-left p-2 border-b border-gray-200">Contagiosa?</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700">
                    <tr><td className="p-2 border-b border-gray-100 font-medium">PPP</td><td className="p-2 border-b border-gray-100">Bolinhas uniformes em fileira na coroa</td><td className="p-2 border-b border-gray-100">Nenhum</td><td className="p-2 border-b border-gray-100">Não</td></tr>
                    <tr><td className="p-2 border-b border-gray-100 font-medium">Glândulas de Tyson</td><td className="p-2 border-b border-gray-100">Pontinhos brancos no frênulo</td><td className="p-2 border-b border-gray-100">Nenhum</td><td className="p-2 border-b border-gray-100">Não</td></tr>
                    <tr><td className="p-2 border-b border-gray-100 font-medium">Fordyce</td><td className="p-2 border-b border-gray-100">Pontos amarelados na haste</td><td className="p-2 border-b border-gray-100">Nenhum</td><td className="p-2 border-b border-gray-100">Não</td></tr>
                    <tr><td className="p-2 border-b border-gray-100 font-medium">Herpes</td><td className="p-2 border-b border-gray-100">Bolhas com líquido, agrupadas</td><td className="p-2 border-b border-gray-100">Dor, ardência, febre</td><td className="p-2 border-b border-gray-100">Sim</td></tr>
                    <tr><td className="p-2 border-b border-gray-100 font-medium">HPV</td><td className="p-2 border-b border-gray-100">Verrugas irregulares, couve-flor</td><td className="p-2 border-b border-gray-100">Às vezes coceira</td><td className="p-2 border-b border-gray-100">Sim</td></tr>
                    <tr><td className="p-2 font-medium">Balanite</td><td className="p-2">Vermelhidão difusa e descamação</td><td className="p-2">Coceira, ardor</td><td className="p-2">Não (mas tratável)</td></tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section id="alerta" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-3">5. Sinais de alerta: procure um médico se houver</h2>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Bolhas com líquido, feridas abertas ou sangramento</li>
                <li>Dor, ardência ao urinar ou secreção uretral</li>
                <li>Crescimento rápido ou mudança de cor e formato</li>
                <li>Coceira intensa, vermelhidão difusa ou odor</li>
                <li>Febre ou íngua na virilha</li>
                <li>Lesões após contato sexual sem preservativo</li>
              </ul>
            </section>

            <section id="remocao" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-3">6. Remoção das bolinhas benignas</h2>
              <p className="text-gray-700 mb-3">
                Quando o diagnóstico é PPP ou glândula de Tyson, a remoção é uma decisão estética, não médica. As opções clínicas
                mais usadas são <strong>laser de CO₂</strong> e <strong>eletrocauterização</strong>, feitas com anestesia local por
                dermatologista ou urologista, com recuperação típica de 1 a 3 semanas.
              </p>
              <p className="text-gray-700">
                Compare métodos, custos e riscos no guia{" "}
                <Link to="/artigos/opcoes-remocao-ppp" className="text-[#4267B2] hover:underline">opções de remoção de PPP</Link> e no{" "}
                <Link to="/guias/pequenas-bolinhas-no-penis" className="text-[#4267B2] hover:underline">guia completo sobre bolinhas no p-nis</Link>.
              </p>
            </section>

            <section id="faq" className="mb-2">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-[#4267B2]" /> 7. Perguntas frequentes
              </h2>
              <dl className="space-y-4">
                {faq.map((item, i) => (
                  <div key={i} className="border border-gray-200 rounded-lg p-4">
                    <dt className="font-semibold text-gray-900 mb-2">
                      <h3>{item.question}</h3>
                    </dt>
                    <dd className="text-sm text-gray-700 leading-relaxed">{item.answer}</dd>
                  </div>
                ))}
              </dl>
            </section>

            <VideoBanner variant="footer" />
          </div>
        </article>

        <aside className="mt-8 bg-[#4267B2]/5 border border-[#4267B2]/20 rounded-lg p-6">
          <h2 className="font-semibold text-gray-900 mb-2">Continue lendo</h2>
          <ul className="text-sm space-y-2">
            <li><Link to="/guias/pequenas-bolinhas-no-penis" className="text-[#4267B2] hover:underline">Guia completo: bolinhas brancas no p-nis</Link></li>
            <li><Link to="/perguntas-frequentes" className="text-[#4267B2] hover:underline">Perguntas frequentes sobre PPP e glândulas de Tyson</Link></li>
            <li><Link to="/artigos" className="text-[#4267B2] hover:underline">Todos os artigos de saúde masculina</Link></li>
          </ul>
        </aside>

        <footer className="text-center py-6 mt-8 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            Conteúdo educativo. Não substitui consulta médica — procure um urologista ou dermatologista em caso de dúvida.
          </p>
        </footer>
      </main>
    </div>
  );
};

export default GlansBumpsGuide;
